import { useCallback, useEffect, useMemo, type FC } from "react";
import {
  AndroidFilled,
  AppleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Checkbox, Radio, Tooltip } from "antd";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { useAffiliateDeepLinkBaseUrl } from "../../../../../hooks/useAffiliateDeepLinkBaseUrl";
import { useIsDevMode } from "../../../../../hooks/useIsDevMode";
import { Button, Form, Input, Modal, Select } from "../../../../shared";
import { isHttpUrl } from "../../../../shared/Form/validators";

import {
  CREATE_PROMO_CODE_INITIAL_VALUES,
  CREATE_PROMO_CODE_REQUIRED_FIELDS,
  getAndroidIfNotInstalledOptions,
  getAndroidLinkBehaviorOptions,
  getBonusSelectionOptions,
  getIosIfNotInstalledOptions,
  getIosLinkBehaviorOptions,
  isPromoCodeFieldFilled,
  type CreatePromoCodeFormValues,
  type RadioOption,
} from "./createPromoCodeModalData";
import { buildAffiliateDeepLinkUrl } from "../promoCodesData";

import styles from "./CreatePromoCodeModal.module.css";

export type CreatePromoCodeModalMode = "create" | "edit";

export type CreatePromoCodeModalProps = {
  open: boolean;
  /**
   * Whether the modal is creating a new code or editing an existing one.
   * Defaults to `"create"`. The title prefix changes accordingly
   * (`"Create Promo Code N"` vs. `"Edit Promo Code N"`); the form fields
   * are identical in both modes.
   */
  mode?: CreatePromoCodeModalMode;
  /** Index used to suffix the modal title (e.g. `"Create Promo Code 1"`). */
  index: number;
  /**
   * Hydrate the form when opening in `"edit"` mode (or to seed a draft in
   * `"create"` mode). Merged on top of `CREATE_PROMO_CODE_INITIAL_VALUES`,
   * so any unset field falls back to the create-mode default.
   */
  initialValues?: Partial<CreatePromoCodeFormValues>;
  onClose: () => void;
  onSubmit: (values: CreatePromoCodeFormValues) => void;
};

const CustomFallbackUrlInput: FC<{
  name: "iosCustomUrl" | "androidCustomUrl";
  testId: string;
}> = ({ name, testId }) => (
  <Form.Item
    name={name}
    rules={[
      {
        required: true,
        message: AppStrings.PAM_AFFILIATE_PROMO_VALIDATION_REQUIRED_CUSTOM_URL,
      },
      Form.validators.noWhitespace(
        AppStrings.PAM_AFFILIATE_PROMO_VALIDATION_REQUIRED_CUSTOM_URL,
      ),
      Form.validators.httpUrl(
        AppStrings.PAM_AFFILIATE_PROMO_VALIDATION_INVALID_CUSTOM_URL,
      ),
    ]}
  >
    <Input
      placeholder={AppStrings.PAM_AFFILIATE_PROMO_CUSTOM_URL_PLACEHOLDER}
      data-testid={testId}
      inputMode="url"
      aria-label={AppStrings.PROMO_CODE_IF_NOT_INSTALLED_CUSTOM_URL}
    />
  </Form.Item>
);

const renderRadioOptions = <TValue extends string>(
  options: ReadonlyArray<RadioOption<TValue>>,
) =>
  options.map((option) => (
    <Radio key={option.value} value={option.value} className={styles.radio}>
      {option.label}
    </Radio>
  ));

const CreatePromoCodeModal: FC<CreatePromoCodeModalProps> = ({
  open,
  mode = "create",
  index,
  initialValues,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm<CreatePromoCodeFormValues>();

  const mergedInitialValues = useMemo(
    () => ({ ...CREATE_PROMO_CODE_INITIAL_VALUES, ...initialValues }),
    [initialValues],
  );

  // antd's `useForm` returns a stable instance whose internal store caches
  // the `initialValues` from the *first* mount. With `destroyOnHidden` the
  // Form remounts on each open, but `resetFields()` still resets to those
  // cached values — so opening the modal in edit mode after a previous
  // create session would surface stale empty fields. Combining
  // `resetFields` (to clear validation state) with `setFieldsValue` (to
  // apply the up-to-date merged values) keeps the form deterministic on
  // every open, regardless of the previous session.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(mergedInitialValues);
    }
  }, [open, form, mergedInitialValues]);

  // The Reward section ships dev-mode-only until the BE bonus catalogue
  // lands (`getBonusSelectionOptions()` is a placeholder). Outside dev mode
  // the section doesn't render AND `bonusSelection` drops out of the Save
  // gate — otherwise an invisible required field would block the form.
  const isDevMode = useIsDevMode();
  const watchedCreateAppsOnAirLink = Form.useWatch(
    "createAppsOnAirLink",
    form,
  );
  const shouldCreateAppsOnAirLink =
    watchedCreateAppsOnAirLink ??
    mergedInitialValues.createAppsOnAirLink ??
    true;

  // Subscribe via a primitive selector — `Object.is` then gates re-renders
  // to just the moments where the gate flips. Full validation still runs on
  // submit; this is purely a CTA hint. Custom-URL inputs are required only
  // when that radio is selected, so they stay off the static list.
  const canSave = Form.useWatch(
    (values) => {
      if (!values) return false;
      const createAppsOnAirLink = values.createAppsOnAirLink !== false;
      const requiredOk = CREATE_PROMO_CODE_REQUIRED_FIELDS.filter((field) => {
        if (!isDevMode && field === "bonusSelection") return false;
        if (
          !createAppsOnAirLink &&
          (field === "appLinkName" || field === "deepLinkUrl")
        ) {
          return false;
        }
        return true;
      }).every((field) => isPromoCodeFieldFilled(values[field]));
      if (!requiredOk) return false;
      if (
        createAppsOnAirLink &&
        values.iosIfNotInstalled === "custom_url" &&
        !isHttpUrl(values.iosCustomUrl)
      ) {
        return false;
      }
      if (
        createAppsOnAirLink &&
        values.androidIfNotInstalled === "custom_url" &&
        !isHttpUrl(values.androidCustomUrl)
      ) {
        return false;
      }
      return true;
    },
    form,
  );

  const title = useMemo(() => {
    const prefix =
      mode === "edit"
        ? AppStrings.NEW_AFFILIATE_EDIT_PROMO_CODE
        : AppStrings.NEW_AFFILIATE_CREATE_PROMO_CODE;
    return `${prefix} ${index}`;
  }, [mode, index]);

  const handleFinish = useCallback(
    (values: CreatePromoCodeFormValues) => {
      // With the Reward section hidden (non-dev), antd omits the unmounted
      // field from `values` — pass the hydrated selection through untouched
      // so a draft's reward survives an edit round-trip.
      onSubmit({
        ...values,
        bonusSelection:
          values.bonusSelection ?? mergedInitialValues.bonusSelection,
      });
    },
    [onSubmit, mergedInitialValues],
  );

  const handleSave = useCallback(() => {
    form.submit();
  }, [form]);

  // Deep link URL is derived from the promo code name — the field is
  // read-only and reflects `https://{deep-link base}/referral_code=<SLUG>`
  // in real time. Watching `name` and writing the derived value back into
  // the form keeps it part of the snapshot that `form.getFieldsValue()`
  // hands to `onSubmit`, so the downstream payload mapper still sees it
  // under `deepLinkUrl` without any extra plumbing.
  //
  // Host comes from `affiliates_config.deeplink_base_url`. When unset the
  // required-rule blocks Save Code — except in dev mode, where we let QA
  // type the URL by hand so they're not blocked by infra.
  const deepLinkBaseUrl = useAffiliateDeepLinkBaseUrl();
  const canEditDeepLinkUrl = isDevMode && !deepLinkBaseUrl;
  const watchedName = Form.useWatch("name", form);
  const derivedDeepLinkUrl = useMemo(
    () => buildAffiliateDeepLinkUrl(watchedName ?? "", deepLinkBaseUrl),
    [watchedName, deepLinkBaseUrl],
  );

  const watchedIosIfNotInstalled = Form.useWatch("iosIfNotInstalled", form);
  const watchedAndroidIfNotInstalled = Form.useWatch(
    "androidIfNotInstalled",
    form,
  );
  const showIosCustomUrl =
    (watchedIosIfNotInstalled ?? mergedInitialValues.iosIfNotInstalled) ===
    "custom_url";
  const showAndroidCustomUrl =
    (watchedAndroidIfNotInstalled ??
      mergedInitialValues.androidIfNotInstalled) === "custom_url";
  const watchedBonusSelection = Form.useWatch("bonusSelection", form);
  const bonusSelectionValues = useMemo(
    () => new Set(watchedBonusSelection ?? []),
    [watchedBonusSelection],
  );

  useEffect(() => {
    // Don't stomp the user's manual input in dev mode.
    if (canEditDeepLinkUrl || !shouldCreateAppsOnAirLink) return;
    form.setFieldValue("deepLinkUrl", derivedDeepLinkUrl);
  }, [
    form,
    derivedDeepLinkUrl,
    canEditDeepLinkUrl,
    shouldCreateAppsOnAirLink,
  ]);

  const footer = (
    <div className={styles.footer}>
      <Button
        intent="primary"
        onClick={handleSave}
        disabled={!canSave}
        data-testid="create-promo-code-save"
      >
        {AppStrings.PROMO_CODE_SAVE_CODE}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      footer={footer}
      destroyOnHidden
      data-testid="create-promo-code-modal"
    >
      <Form<CreatePromoCodeFormValues>
        form={form}
        initialValues={mergedInitialValues}
        onFinish={handleFinish}
        className={styles.form}
        data-testid="create-promo-code-form"
      >
        <Form.Item
          name="name"
          label={`${AppStrings.PROMO_CODE_NAME}*`}
          rules={[
            {
              required: true,
              message: AppStrings.PROMO_CODE_VALIDATION_REQUIRED_NAME,
            },
            Form.validators.noWhitespace(
              AppStrings.PROMO_CODE_VALIDATION_REQUIRED_NAME,
            ),
            {
              max: 16,
              message: AppStrings.PROMO_CODE_VALIDATION_MAX_LENGTH_NAME,
            },
          ]}
        >
          <Input
            placeholder={AppStrings.PROMO_CODE_NAME_PLACEHOLDER}
            data-testid="create-promo-code-name"
            maxLength={16}
          />
        </Form.Item>

        {/* TODO(BE): unhide once the bonus catalogue is supported. */}
        {isDevMode && (
          <section
            className={styles.section}
            aria-labelledby="create-promo-code-reward-title"
          >
            <div className={styles.sectionHeader}>
              <h3
                id="create-promo-code-reward-title"
                className={styles.sectionTitle}
              >
                {AppStrings.PROMO_CODE_REWARD}
              </h3>
              <Tooltip title={AppStrings.PROMO_CODE_REWARD_TOOLTIP}>
                <InfoCircleOutlined
                  className={styles.infoIcon}
                  aria-label={AppStrings.PROMO_CODE_REWARD_INFO_LABEL}
                  data-testid="create-promo-code-reward-info"
                />
              </Tooltip>
            </div>

            <Form.Item
              name="bonusSelection"
              // Custom label so the persistent "At least 1 option required"
              // hint can sit on the right of the row. antd accepts any JSX
              // here and renders it inside the existing `<label>` slot —
              // the per-item class widens the label to full width so the
              // flex distribution actually has room to spread.
              className={styles.bonusSelectionItem}
              label={
                <span className={styles.bonusSelectionLabel}>
                  <span>{AppStrings.PROMO_CODE_BONUS_SELECTION}</span>
                  <span
                    className={styles.bonusSelectionHint}
                    data-testid="create-promo-code-bonus-selection-hint"
                  >
                    {AppStrings.PROMO_CODE_VALIDATION_REQUIRED_BONUS_SELECTION}
                  </span>
                </span>
              }
              rules={[
                {
                  required: true,
                  // antd accepts an empty array as "absent" for required-array
                  // fields, so this single rule covers both "never touched" and
                  // "all chips removed".
                  message:
                    AppStrings.PROMO_CODE_VALIDATION_REQUIRED_BONUS_SELECTION,
                },
              ]}
            >
              <Select<string[]>
                mode="multiple"
                placeholder={AppStrings.PROMO_CODE_BONUS_SELECTION_PLACEHOLDER}
                options={[...getBonusSelectionOptions()]}
                data-testid="create-promo-code-bonus-selection"
                // `maxTagCount={0}` funnels every selection into the single
                // "rest" slot so the trigger reads as comma-separated text
                // instead of antd's removable chip tags.
                maxTagCount={0}
                maxTagPlaceholder={(omitted) =>
                  omitted.map((item) => item.label).join(", ")
                }
                menuItemSelectedIcon={null}
                popupClassName={styles.bonusSelectionPopup}
                optionRender={(option) => (
                  <span className={styles.bonusSelectionOption}>
                    {/* Presentational only — the option row handles the click.
                      `onChange` is a noop to silence React's controlled-input warning. */}
                    <Checkbox
                      checked={bonusSelectionValues.has(String(option.value))}
                      tabIndex={-1}
                      onChange={() => {}}
                    />
                    <span>{option.label}</span>
                  </span>
                )}
              />
            </Form.Item>
          </section>
        )}

        <Form.Item name="createAppsOnAirLink" valuePropName="checked">
          <Checkbox
            className={styles.appsOnAirCheckbox}
            data-testid="create-promo-code-create-appsonair-link"
          >
            {AppStrings.PAM_AFFILIATE_PROMO_CREATE_APPSONAIR_LINK}
          </Checkbox>
        </Form.Item>

        {shouldCreateAppsOnAirLink && (
          <>
        <section
          className={styles.section}
          aria-labelledby="create-promo-code-applink-title"
        >
          <div className={styles.sectionHeader}>
            <h3
              id="create-promo-code-applink-title"
              className={styles.sectionTitle}
            >
              {AppStrings.PROMO_CODE_SET_UP_APP_LINK}
            </h3>
            <Tooltip title={AppStrings.PROMO_CODE_SET_UP_APP_LINK_TOOLTIP}>
              <InfoCircleOutlined
                className={styles.infoIcon}
                aria-label={AppStrings.PROMO_CODE_SET_UP_APP_LINK_INFO_LABEL}
                data-testid="create-promo-code-applink-info"
              />
            </Tooltip>
          </div>

          <div className={styles.row}>
            <Form.Item
              name="appLinkName"
              label={`${AppStrings.PROMO_CODE_APP_LINK_NAME}*`}
              rules={[
                {
                  required: true,
                  message:
                    AppStrings.PROMO_CODE_VALIDATION_REQUIRED_APP_LINK_NAME,
                },
                Form.validators.noWhitespace(
                  AppStrings.PROMO_CODE_VALIDATION_REQUIRED_APP_LINK_NAME,
                ),
              ]}
            >
              <Input
                placeholder={AppStrings.PROMO_CODE_APP_LINK_NAME_PLACEHOLDER}
                data-testid="create-promo-code-applink-name"
              />
            </Form.Item>

            <Form.Item
              name="deepLinkUrl"
              label={`${AppStrings.PROMO_CODE_DEEP_LINK_URL}*`}
              rules={[
                {
                  required: true,
                  message:
                    AppStrings.PROMO_CODE_VALIDATION_REQUIRED_DEEP_LINK_URL,
                },
              ]}
            >
              <Input
                placeholder={AppStrings.PROMO_CODE_DEEP_LINK_URL_PLACEHOLDER}
                data-testid="create-promo-code-deep-link-url"
                inputMode="url"
                disabled={!canEditDeepLinkUrl}
              />
            </Form.Item>
          </div>
        </section>

        <section
          className={styles.section}
          aria-labelledby="create-promo-code-ios-title"
        >
          <div className={styles.sectionHeader}>
            <AppleFilled className={styles.platformIcon} aria-hidden="true" />
            <h3
              id="create-promo-code-ios-title"
              className={styles.sectionTitle}
            >
              {AppStrings.PROMO_CODE_IOS}
            </h3>
          </div>

          <div className={styles.radioRow}>
            <Form.Item
              name="iosLinkBehavior"
              label={AppStrings.PROMO_CODE_DEFINE_LINK_BEHAVIOR_IOS}
              className={styles.radioItem}
            >
              <Radio.Group
                className={styles.radioGroup}
                data-testid="create-promo-code-ios-link-behavior"
              >
                {renderRadioOptions(getIosLinkBehaviorOptions())}
              </Radio.Group>
            </Form.Item>

            <div className={styles.fallbackColumn}>
              <Form.Item
                name="iosIfNotInstalled"
                label={AppStrings.PROMO_CODE_IF_NOT_INSTALLED}
              >
                <Radio.Group
                  className={styles.radioGroup}
                  data-testid="create-promo-code-ios-if-not-installed"
                >
                  {renderRadioOptions(getIosIfNotInstalledOptions())}
                </Radio.Group>
              </Form.Item>
              {showIosCustomUrl && (
                <CustomFallbackUrlInput
                  name="iosCustomUrl"
                  testId="create-promo-code-ios-custom-url"
                />
              )}
            </div>
          </div>
        </section>

        <section
          className={styles.section}
          aria-labelledby="create-promo-code-android-title"
        >
          <div className={styles.sectionHeader}>
            <AndroidFilled className={styles.platformIcon} aria-hidden="true" />
            <h3
              id="create-promo-code-android-title"
              className={styles.sectionTitle}
            >
              {AppStrings.PROMO_CODE_ANDROID}
            </h3>
          </div>

          <div className={styles.radioRow}>
            <Form.Item
              name="androidLinkBehavior"
              label={AppStrings.PROMO_CODE_DEFINE_LINK_BEHAVIOR_ANDROID}
              className={styles.radioItem}
            >
              <Radio.Group
                className={styles.radioGroup}
                data-testid="create-promo-code-android-link-behavior"
              >
                {renderRadioOptions(getAndroidLinkBehaviorOptions())}
              </Radio.Group>
            </Form.Item>

            <div className={styles.fallbackColumn}>
              <Form.Item
                name="androidIfNotInstalled"
                label={AppStrings.PROMO_CODE_IF_NOT_INSTALLED}
              >
                <Radio.Group
                  className={styles.radioGroup}
                  data-testid="create-promo-code-android-if-not-installed"
                >
                  {renderRadioOptions(getAndroidIfNotInstalledOptions())}
                </Radio.Group>
              </Form.Item>
              {showAndroidCustomUrl && (
                <CustomFallbackUrlInput
                  name="androidCustomUrl"
                  testId="create-promo-code-android-custom-url"
                />
              )}
            </div>
          </div>
        </section>

        <section
          className={styles.section}
          aria-labelledby="create-promo-code-social-title"
        >
          <div className={styles.sectionHeader}>
            <h3
              id="create-promo-code-social-title"
              className={styles.sectionTitle}
            >
              {AppStrings.PROMO_CODE_ADD_SOCIAL_META_TAGS}
            </h3>
            <Tooltip title={AppStrings.PROMO_CODE_ADD_SOCIAL_META_TAGS_TOOLTIP}>
              <InfoCircleOutlined
                className={styles.infoIcon}
                aria-label={
                  AppStrings.PROMO_CODE_ADD_SOCIAL_META_TAGS_INFO_LABEL
                }
                data-testid="create-promo-code-social-info"
              />
            </Tooltip>
          </div>

          <div className={styles.row}>
            <Form.Item
              name="previewTitle"
              label={AppStrings.PROMO_CODE_PREVIEW_TITLE}
            >
              <Input
                placeholder={AppStrings.PROMO_CODE_PREVIEW_TITLE_PLACEHOLDER}
                data-testid="create-promo-code-preview-title"
              />
            </Form.Item>

            <Form.Item
              name="previewImageUrl"
              label={AppStrings.PROMO_CODE_PREVIEW_IMAGE_URL}
            >
              <Input
                placeholder={
                  AppStrings.PROMO_CODE_PREVIEW_IMAGE_URL_PLACEHOLDER
                }
                data-testid="create-promo-code-preview-image-url"
                inputMode="url"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="previewDescription"
            label={AppStrings.PROMO_CODE_PREVIEW_DESCRIPTION}
          >
            <Input
              placeholder={
                AppStrings.PROMO_CODE_PREVIEW_DESCRIPTION_PLACEHOLDER
              }
              data-testid="create-promo-code-preview-description"
            />
          </Form.Item>
        </section>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default CreatePromoCodeModal;
